/**
 * Error Boundary specific to Directory Page
 *
 * Catches errors in DirectoryPage component tree and provides fallback UI
 * with mock data option and debugging information.
 */

import React from 'react';
import { captureReactException } from '@sentry/react';
import { Card, Alert, Button, Typography, Space } from 'antd';
import { ReloadOutlined, BugOutlined } from '@ant-design/icons';

const { Title, Paragraph, Text } = Typography;

interface DirectoryErrorBoundaryState {
  hasError: boolean;
  error?: Error;
  errorInfo?: React.ErrorInfo;
  showDetails: boolean;
}

interface DirectoryErrorBoundaryProps {
  children: React.ReactNode;
  fallbackMessage?: string;
}

class DirectoryErrorBoundary extends React.Component<
  DirectoryErrorBoundaryProps,
  DirectoryErrorBoundaryState
> {
  constructor(props: DirectoryErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      showDetails: false
    };
  }

  static getDerivedStateFromError(error: Error): Partial<DirectoryErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('DirectoryErrorBoundary caught an error:', error, errorInfo);
    // No-op unless initSentry() enabled reporting (src/monitoring/sentry.ts).
    captureReactException(error, errorInfo);
    this.setState({ errorInfo });
  }

  handleReload = () => {
    // Try to reload the page
    window.location.reload();
  };

  handleReset = () => {
    // Reset error boundary state
    this.setState({
      hasError: false,
      error: undefined,
      errorInfo: undefined,
      showDetails: false
    });
  };

  toggleDetails = () => {
    this.setState(prev => ({ showDetails: !prev.showDetails }));
  };

  render() {
    if (this.state.hasError) {
      const { error, errorInfo, showDetails } = this.state;
      const { fallbackMessage } = this.props;

      return (
        <div style={{ padding: '24px', maxWidth: '800px', margin: '0 auto' }}>
          <Card>
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
              <div style={{ textAlign: 'center' }}>
                <BugOutlined style={{ fontSize: '48px', color: '#ff4d4f' }} />
                <Title level={2} style={{ marginTop: '16px' }}>
                  Directory Page Error
                </Title>
                <Paragraph type="secondary">
                  {fallbackMessage ||
                    'An error occurred while loading the employee directory. This could be due to API connectivity issues or a component error.'
                  }
                </Paragraph>
              </div>

              <Alert
                message="What you can try:"
                description={
                  <ul style={{ marginBottom: 0 }}>
                    <li>Refresh the page to reload the directory</li>
                    <li>Check your network connection</li>
                    <li>Try again in a few moments</li>
                    <li>Contact support if the problem persists</li>
                  </ul>
                }
                type="info"
                showIcon
              />

              <div style={{ textAlign: 'center' }}>
                <Space>
                  <Button type="primary" icon={<ReloadOutlined />} onClick={this.handleReload}>
                    Reload Page
                  </Button>
                  <Button onClick={this.handleReset}>
                    Try Again
                  </Button>
                  <Button onClick={this.toggleDetails}>
                    {showDetails ? 'Hide Details' : 'Show Details'}
                  </Button>
                </Space>
              </div>

              {showDetails && error && (
                <Card title="Error Details" size="small">
                  <div style={{ fontFamily: 'monospace', fontSize: '12px' }}>
                    <div>
                      <strong>Error:</strong> {error.message}
                    </div>
                    <div>
                      <strong>Stack:</strong>
                      <pre style={{
                        fontSize: '11px',
                        background: '#f5f5f5',
                        padding: '8px',
                        marginTop: '4px',
                        overflow: 'auto',
                        maxHeight: '200px'
                      }}>
                        {error.stack}
                      </pre>
                    </div>
                    {errorInfo && (
                      <div>
                        <strong>Component Stack:</strong>
                        <pre style={{
                          fontSize: '11px',
                          background: '#f5f5f5',
                          padding: '8px',
                          marginTop: '4px',
                          overflow: 'auto',
                          maxHeight: '200px'
                        }}>
                          {errorInfo.componentStack}
                        </pre>
                      </div>
                    )}
                  </div>
                </Card>
              )}

              <Alert
                message="Development Mode"
                description={
                  <div>
                    <p>The directory page has been enhanced with:</p>
                    <ul style={{ marginBottom: 0 }}>
                      <li>Mock data fallback when API is unavailable</li>
                      <li>Enhanced error handling and debugging</li>
                      <li>Comprehensive search and filtering</li>
                      <li>Responsive design for all screen sizes</li>
                    </ul>
                  </div>
                }
                type="success"
                showIcon
              />
            </Space>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}

export default DirectoryErrorBoundary;